import { useMemo, memo, forwardRef } from 'react';
import { cn } from '@restworld/utils-common';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import { OptionsType, CircleMinusIcon } from '@restworld/utils-common';

import { IconComponentType } from '../icon-button';
import { focusRing } from '../styles/focus-ring';

const COLORS = {
  neutral200: '#666676',
  neutral700: '#1c1c33',
  purple700: '#482485',
};

export const ChipCheckBox = memo((props: IChipCheckBoxProps) => {
  // `returnKey`/`iconElement` come from OptionsType and are not valid DOM attributes; strip
  // them so `{...rest}` on the <input> below doesn't leak them (React warned on `returnKey`).
  const {
    id,
    icon,
    label,
    checked,
    onChangeHandler,
    testId,
    disabled,
    Icon,
    wrapperStyles,
    returnKey,
    iconElement,
    ...rest
  } = props;

  const iconEl = useMemo(() => {
    const iconClasses = 'h-4 w-4 min-h-[16px] min-w-[16px]';

    return Icon ? (
      <Icon
        className={iconClasses}
        stroke={disabled ? COLORS.neutral200 : checked ? COLORS.purple700 : COLORS.neutral700}
      />
    ) : checked ? (
      <CheckCircleIcon className={iconClasses} fill={disabled ? COLORS.neutral200 : COLORS.purple700} />
    ) : (
      <CircleMinusIcon className={iconClasses} fill={disabled ? COLORS.neutral200 : COLORS.neutral700} />
    );
  }, [Icon, checked, disabled]);
  const inputEl = useMemo(
    () => (
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          if (onChangeHandler) onChangeHandler(id);
        }}
        disabled={disabled}
        className="hidden"
        {...rest}
      />
    ),
    [checked, disabled, id, onChangeHandler, rest]
  );
  return (
    <label
      data-cy={testId}
      key={id}
      className={cn(
        `group inline-flex gap-2 items-center rounded-lg py-2 px-3 !h-12 cursor-pointer border border-neutral-90 bg-neutral-10 hover:bg-neutral-30 ${focusRing}`,
        checked && 'bg-purple-50 border-purple-700 text-purple-700 hover:bg-purple-50',
        disabled && 'cursor-not-allowed bg-neutral-40 border-neutral-40 hover:bg-neutral-40',
        wrapperStyles
      )}
    >
      {iconEl}
      {inputEl}
      <h5
        className={cn(
          'font-medium !text-base text-neutral-700 w-full truncate',
          checked && 'text-purple-700',
          disabled && 'text-neutral-200'
        )}
      >
        {label}
      </h5>
    </label>
  );
});

export const ChipCheckboxGroup = forwardRef(
  (
    {
      options,
      onChangeHandler,
      value,
      customChipGroupStyles,
      testId,
      chipStyle,
      ...rest
    }: {
      options: GroupOptionsType[];
      value: string[];
      onChangeHandler?: (x: string) => void;
      customChipGroupStyles?: string;
      testId?: string;
      chipStyle?: string;
    },
    ref?: React.Ref<HTMLInputElement>
  ) => (
    <div
      data-cy={testId}
      className={cn('flex flex-row gap-2 flex-wrap', customChipGroupStyles)}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      {options?.map((chip, index) => {
        // `chip.key` is a data field, not a React key. Spreading it re-supplied `key` to the
        // JSX element and triggered React's "key prop spread" warning. Pull it out so only the
        // explicit `key={index}` below is used.
        const { key: chipKey, ...chipProps } = chip;
        return (
          <ChipCheckBox
            key={index.toString()}
            testId={`${chipKey}`}
            {...rest}
            {...{ ...chipProps, onChangeHandler }}
            checked={Array.isArray(value) ? value?.indexOf(chip.id) > -1 : value === chip.id}
            disabled={chip.disabled}
            wrapperStyles={chipStyle}
          />
        );
      })}
    </div>
  )
);

interface IChipCheckBoxProps extends OptionsType {
  checked: boolean;
  onChangeHandler?: (x: string) => void;
  testId?: string;
  disabled?: boolean;
  Icon?: IconComponentType;
  wrapperStyles?: string;
}
export type GroupOptionsType = OptionsType & { Icon?: IconComponentType; disabled?: boolean };
